/**
 * Service admin de badges — espelha 1:1 o backend (10 endpoints HTTP).
 *
 * Esta sprint cobre os 5 endpoints CRUD básicos:
 *   GET    /admin/badges            — useAdminBadges
 *   GET    /admin/badges/:id        — useBadge
 *   POST   /admin/badges            — useCreateBadge
 *   PATCH  /admin/badges/:id        — useUpdateBadge
 *   DELETE /admin/badges/:id        — useDeleteBadge  (soft delete via SPK-1)
 *
 * Fora desta sprint (próxima rodada quando vier UI de conquistas):
 *   POST   /admin/badges/:id/grant/:userId
 *   DELETE /admin/badges/:id/revoke/:userId
 *   GET    /admin/badges/:id/recipients
 *   GET    /admin/badges/ranking
 *
 * Multi-tenancy: o back já trata isolation via JWT (`cid`). Admin local
 * sempre escopo trancado; super_admin pode global ou per-company.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'
import type { Badge, BadgeDraft, Tier } from '@/components/super-admin/badges/types'

// Filtros aceitos pelo back em GET /admin/badges (validados via Zod
// `listBadgesQuerySchema`). Front omite chaves undefined antes de mandar.
export interface ListBadgesFilters {
  tier?: Tier
  active?: boolean
  companyId?: string // ignorado silenciosamente se admin local
  scope?: 'global' | 'company' | 'all'
  search?: string
}

// PATCH aceita qualquer subset do draft + `active` (toggle sem precisar
// passar pelo soft delete). `id`, `createdBy`, `createdAt`, `updatedAt`
// nunca chegam no body.
export type UpdateBadgePatch = Partial<BadgeDraft>

/**
 * Resumo da concessão em lote (critério `manual_roster`). O back anexa
 * isso na resposta de POST/PATCH quando `recipientEmails` foi enviado.
 */
export interface RosterGrantSummary {
  granted: number
  alreadyHad: number
  notFound: string[]
  skipped: number
}

/** Resposta de create/update — badge + resumo do roster (se houve). */
export type BadgeWithRoster = Badge & { rosterGrant?: RosterGrantSummary }

const ADMIN_BADGES_KEY = ['admin-badges'] as const

/**
 * Remove chaves com valor undefined de um objeto.
 * Necessário pra evitar que o axios serialize `?tier=undefined&...`.
 */
function stripUndefined<T extends object>(obj: T | undefined): Partial<T> {
  if (!obj) return {}
  const out: Partial<T> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) {
      ;(out as Record<string, unknown>)[k] = v
    }
  }
  return out
}

export function useAdminBadges(filters?: ListBadgesFilters) {
  return useQuery({
    queryKey: [...ADMIN_BADGES_KEY, filters],
    queryFn: async () => {
      const params = stripUndefined(filters)
      const response = await api.get<Badge[]>('/admin/badges', { params })
      return response.data
    },
  })
}

export function useBadge(id: string) {
  return useQuery({
    queryKey: [...ADMIN_BADGES_KEY, 'detail', id],
    queryFn: async () => {
      const response = await api.get<Badge>(`/admin/badges/${id}`)
      return response.data
    },
    enabled: id.length > 0,
  })
}

export function useCreateBadge() {
  const queryClient = useQueryClient()

  return useMutation({
    // `recipientEmails` só vai no body pro critério `manual_roster` —
    // o back concede a badge em lote e devolve `rosterGrant`.
    mutationFn: async (input: BadgeDraft & { recipientEmails?: string[] }) => {
      const response = await api.post<BadgeWithRoster>('/admin/badges', input)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_BADGES_KEY })
    },
  })
}

export function useUpdateBadge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string
      patch: UpdateBadgePatch & { recipientEmails?: string[] }
    }) => {
      const response = await api.patch<BadgeWithRoster>(`/admin/badges/${id}`, patch)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_BADGES_KEY })
    },
  })
}

export function useDeleteBadge() {
  const queryClient = useQueryClient()

  return useMutation({
    // Soft delete (SPK-1): back retorna a badge com `active=false`.
    // Idempotente — rodar 2× retorna 200 ambas vezes.
    mutationFn: async (id: string) => {
      const response = await api.delete<Badge>(`/admin/badges/${id}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_BADGES_KEY })
    },
  })
}
